// GENERAL
import { useMemo } from "react";
import { useData } from "./../DataContext";
import { useDisciplineFilter } from "./../DisciplineContext";
import { useCountryFilter } from "./../CountryContext";
import {
  CATEGORY_LABEL,
  DISCIPLINE_LABEL,
  EMPTY_KEYS,
  PROGRAM_LABEL,
  REGION_TO_CODE,
  STANDARD_LABELS_BY_GROUP,
  SUBCATEGORY_LABEL,
} from "./mapers";
import {
  cleanRegionKey,
  countManyToArray,
  countToArray,
  formatTitle,
  freqBy,
  getUniversityData,
  groupBy,
  regionFreqToCode,
  safeDate,
  toArray,
} from "./utils";

function groupWithNames(rows, keyFn) {
  return Array.from(groupBy(rows, keyFn).entries())
    .map(([id, list]) => ({
      id,
      count: list.length,
      list: list
        .map((p) => `${p?.nombre ?? ""} ${p?.apellido ?? ""}`.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => b.count - a.count);
}

function cleanHtml(value) {
  return String(value ?? "")
    .trim()
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}



function programLabels(raw) {
  const keys = (Array.isArray(raw) ? raw : String(raw ?? "").split(","))
    .map((x) => String(x ?? "").trim().toLowerCase())
    .filter(Boolean);

  return (keys.length ? keys : ["—"]).map((key) => PROGRAM_LABEL[key] ?? formatTitle(key));
}

function userStandards(user) {
  const selectedKeys = new Set(
    (Array.isArray(user?.estandares)
      ? user.estandares
      : String(user?.estandares ?? "").split(",")
    )
      .map((x) => String(x ?? "").trim())
      .filter(Boolean)
  );

  return Object.fromEntries(
    Object.entries(STANDARD_LABELS_BY_GROUP).map(([group, labels]) => {
      const groupKeys = Object.keys(labels).filter((key) => selectedKeys.has(key));

      return [
        group,
        {
          keys: groupKeys,
          labels: groupKeys.map((key) => labels[key]),
        },
      ];
    })
  );
}

function prettyPrograma(raw) {
  return programLabels(raw).join(", ");
}

function groupProgramaCountList(rows) {
  const map = new Map();

  rows.forEach((row) => {
    const nombre = `${row?.nombre ?? ""} ${row?.apellido ?? ""}`.trim();

    programLabels(row?.programa).forEach((label) => {
      if (!map.has(label)) map.set(label, new Set());
      if (nombre) map.get(label).add(nombre);
    });
  });

  return Array.from(map.entries())
    .map(([id, set]) => ({
      id,
      count: set.size,
      list: Array.from(set).sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => b.count - a.count);
}

function degreeKey(value) {
  const text = String(value ?? "").trim().toLowerCase();

  if (!text || EMPTY_KEYS.has(text.toUpperCase())) return "—";
  if (text.includes("licenciatura")) return "licenciatura";
  if (text.includes("magister") || text.includes("magíster")) return "magister";
  if (text.includes("doctorado") || text.includes("doctor")) return "doctorado";

  return text;
}

function degreeLabel(value) {
  const key = degreeKey(value);

  if (key === "licenciatura") return "Licenciatura";
  if (key === "magister") return "Magíster";
  if (key === "doctorado") return "Doctorado";

  return key === "—" ? "—" : formatTitle(key);
}

function levelFromYears(years) {
  if (years == null || Number.isNaN(years)) return "—";
  if (years <= 5) return "novel";
  if (years <= 11) return "intermedio";
  return "experto";
}

function userDisciplines(user) {
  const disciplines = [];
  if (user?.is_formador_matematica) disciplines.push("matematica");
  if (user?.is_formador_lenguaje) disciplines.push("lenguaje");
  if (user?.is_formador_parvularia) disciplines.push("parvularia");
  return disciplines;
}

function eventDisciplines(event) {
  const disciplines = [];
  if (event?.is_matematica) disciplines.push("matematica");
  if (event?.is_lenguaje) disciplines.push("lenguaje");
  if (event?.is_parvularia) disciplines.push("parvularia");
  return disciplines;
}

function disciplineText(keys) {
  return keys.length
    ? keys.map((key) => DISCIPLINE_LABEL[key] ?? formatTitle(key)).join(", ")
    : "—";
}

function userUniversities(user) {
  const map = new Map();

  [user?.university, user?.universities_working_at]
    .flatMap((value) =>
      String(value ?? "")
        .trim()
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    )
    .map(getUniversityData)
    .filter(Boolean)
    .forEach((uni) => {
      if (!map.has(uni.key)) map.set(uni.key, uni);
    });

  return Array.from(map.values());
}

function cleanEvent(event) {
  const categoryKey = String(event?.category ?? "").trim().toLowerCase() || "other";
  const subcategoryKey = String(event?.subcategory ?? "").trim().toLowerCase() || "other";
  const title =
    String(event?.title ?? "")
      .trim()
      .replace(/^webinar\s*[:\-–—]\s*/i, "") || "—";
  const description =
    cleanHtml(event?.short_description ?? event?.description)
      .replace(/^webinar\s*[:\-–—]\s*/i, "") || "—";
  const start = event?.start_date ?? null;
  const end = event?.end_date ?? null;
  const disciplines = eventDisciplines(event);

  return {
    ...event,

    id: String(event?.id ?? event?.event_id ?? "").trim(),
    event_id: String(event?.id ?? event?.event_id ?? "").trim(),

    title,
    titulo: title,

    description,
    descripcion: description,

    category: CATEGORY_LABEL[categoryKey] ?? formatTitle(categoryKey),
    categoria: CATEGORY_LABEL[categoryKey] ?? formatTitle(categoryKey),
    category_key: categoryKey,

    subcategory: SUBCATEGORY_LABEL[subcategoryKey] ?? formatTitle(subcategoryKey),
    subcategoria: SUBCATEGORY_LABEL[subcategoryKey] ?? formatTitle(subcategoryKey),
    subcategory_key: subcategoryKey,
    subcategoria_original: event?.subcategory ?? "—",

    bucket: subcategoryKey,

    start_date: start,
    end_date: end,
    inicio: start,
    fin: end,
    year: safeDate(start)?.getFullYear() ?? null,

    place: String(event?.place ?? "").trim() || "—",
    lugar: String(event?.place ?? "").trim() || "—",

    expositor: String(event?.expositor ?? event?.relator ?? "").trim() || "—",
    expositors: (Array.isArray(event?.expositors) ? event.expositors : [])
      .map((expositor) =>
        String(
          expositor?.user_id ??
          expositor?.user?.id ??
          expositor
        ).trim()
      )
      .filter(Boolean),

    expositors_data: (Array.isArray(event?.expositors) ? event.expositors : [])
      .map((expositor) => ({
        user_id: String(
          expositor?.user_id ??
          expositor?.user?.id ??
          expositor
        ).trim(),
        name: String(expositor?.name ?? "").trim(),
        id: String(expositor?.id ?? "").trim(),
      }))
      .filter((expositor) => expositor.user_id),

    disciplinas: disciplines,
    disciplina: disciplineText(disciplines),
  };
}

function encuentroBaseTitle(title) {
  return (
    String(title ?? "")
      .trim()
      .replace(/\((?:primer|primero|1er|1ro)\s*d[ií]a\)/gi, "")
      .replace(/\((?:segundo|2do|2º)\s*d[ií]a\)/gi, "")
      .replace(/\((?:tercer|3er|3º)\s*d[ií]a\)/gi, "")
      .replace(/\((?:cuarto|4to|4º)\s*d[ií]a\)/gi, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase() || "—"
  );
}

function encuentroDayRank(title) {
  const text = String(title ?? "").trim().toLowerCase();

  if (/primer|1er|1ro/.test(text) && text.includes("día")) return 0;
  if (/segundo|2do|2º/.test(text) && text.includes("día")) return 1;
  if (/tercer|3er|3º/.test(text) && text.includes("día")) return 2;

  return 9;
}

function encuentroDedupKey(event) {
  return `${encuentroBaseTitle(event?.titulo)}__${event?.bucket}__${(Array.isArray(event?.disciplinas) ? event.disciplinas : [])
    .sort()
    .join("|")}`;
}

function pickEncuentros(events) {
  const picked = new Map();

  events
    .filter((event) => event.bucket === "encuentro-nacional" || event.bucket === "encuentro-virtual")
    .forEach((event) => {
      const key = encuentroDedupKey(event);
      const current = picked.get(key);

      if (!current) {
        picked.set(key, event);
        return;
      }

      const rank = encuentroDayRank(event.titulo);
      const currentRank = encuentroDayRank(current.titulo);
      const time = safeDate(event.inicio)?.getTime() ?? Number.POSITIVE_INFINITY;
      const currentTime = safeDate(current.inicio)?.getTime() ?? Number.POSITIVE_INFINITY;

      if (rank < currentRank || (rank === currentRank && time < currentTime)) {
        picked.set(key, event);
      }
    });

  return picked;
}

function parsePresentedIn(achievementName) {
  const text = String(achievementName ?? "").trim();
  const lower = text.toLowerCase();

  if (!lower.includes("present") || !lower.includes("encuentro")) return null;

  return (
    String(text.match(/present[oó]\s+en\s*:?\s*(.+)$/i)?.[1] ?? "")
      .trim()
      .replace(/^(el|la|los|las)\s+/i, "")
      .replace(/[.。]+$/g, "")
      .trim() || null
  );
}

function parseEncuentroName(achievementName) {
  return (
    String(achievementName ?? "")
      .trim()
      .match(/\b([IVXLCDM]+)\s+encuentro\s+nacional\b/i)?.[0]
      ?.replace(/\s+/g, " ") ?? null
  );
}

function flattenAchievements(rows) {
  return rows
    .flatMap((row) => {
      const userId = String(row?.user_id ?? row?.id ?? row?.user ?? "").trim();
      const achievements = Array.isArray(row?.achievements)
        ? row.achievements
        : Array.isArray(row?.logros)
          ? row.logros
          : Array.isArray(row?.items)
            ? row.items
            : [];

      return achievements.map((achievement) => ({
        user_id: userId,
        achievement_id: String(achievement?.id ?? achievement?.achievement_id ?? "").trim(),
        event_id: String(achievement?.event_id ?? "").trim(),
        name: String(achievement?.name ?? achievement?.title ?? achievement?.label ?? "").trim(),
        type: String(achievement?.type ?? "").trim(),
        subtype: String(achievement?.subtype ?? "").trim(),
        system_key: String(achievement?.system_key ?? "").trim(),
      }));
    })
    .filter((achievement) => achievement.user_id);
}

function flattenCourses(coursesRaw) {
  const courseEntries = Array.isArray(coursesRaw)
    ? coursesRaw.map((course, index) => [String(course?.id ?? course?.key ?? index).trim(), course])
    : Object.entries(coursesRaw ?? {});

  return courseEntries.map(([courseKey, course]) => {
    const sectionEntries = Array.isArray(course?.sections)
      ? course.sections.map((section, index) => [String(section?.id ?? section?.course_id ?? index).trim(), section])
      : Object.entries(course?.sections ?? {});

    return {
      course_key: String(courseKey ?? "").trim(),
      name: String(course?.name ?? "").trim() || "—",
      type: String(course?.type ?? "general").trim().toLowerCase() || "general",
      description: cleanHtml(course?.description),

      sections: sectionEntries.map(([sectionKey, section]) => {
        const data = section?.data ?? section ?? {};
        const start = data?.start ?? section?.start ?? null;

        return {
          section_key: String(sectionKey ?? "").trim(),
          course_id: String(section?.course_id ?? data?.course_id ?? data?.id ?? "").trim(),
          title: String(section?.title ?? data?.name ?? `Versión ${sectionKey}`).trim(),
          year: String(section?.year ?? safeDate(start)?.getFullYear() ?? "").trim(),
          special_components: section?.special_components ?? {},
          data,
          enrolled_usernames: (Array.isArray(data?.enrolled) ? data.enrolled : [])
            .map((username) => String(username ?? "").trim().toLowerCase())
            .filter(Boolean),
          approved_usernames: (Array.isArray(data?.approved) ? data.approved : [])
            .map((username) => String(username ?? "").trim().toLowerCase())
            .filter(Boolean),
        };
      }),
    };
  });
}

function buildEventItem(event) {
  return {
    id: event.id,
    titulo: event.titulo,
    descripcion: event.descripcion,
    inicio: event.inicio,
    fin: event.fin,
    year: event.year,
    lugar: event.lugar,
    categoria: event.categoria,
    categoria_key: event.category_key,
    subcategoria: event.subcategoria,
    subcategoria_key: event.subcategory_key,
    expositor: event.expositor,
    expositor_id: event.expositors,
    expositors: event.expositors,
    expositors_data: event.expositors_data ?? [],
    disciplinas: event.disciplinas,
    disciplina: event.disciplina,
    bucket: event.bucket,
    tipo_encuentro:
      event.bucket === "encuentro-nacional"
        ? "nacional"
        : event.bucket === "encuentro-virtual"
          ? "virtual"
          : null,
  };
}

export function useRedfidViews() {
  const { loading, error, data, refreshData } = useData();
  const { discipline } = useDisciplineFilter();
  const { country } = useCountryFilter();

  const result = useMemo(() => {
    const users = toArray(data?.users);
    const eventsRaw = toArray(data?.events);
    const attendants = toArray(data?.attendants);
    const allAchievements = toArray(data?.all_achievements);
    const coursesRaw = data?.courses ?? {};

    const formadores = users
      .filter((user) => user?.is_formador && user?.is_active)
      .filter((user) => discipline === "general" || userDisciplines(user).includes(discipline))
      .filter((user) => {
        const pais = String(user?.pais ?? "").trim().toLowerCase();
        return country === "chile" ? !pais || pais === "chile" : pais && pais !== "chile";
      });

    const formadorById = new Map(
      formadores.map((user) => [String(user?.id ?? user?.user_id ?? "").trim(), user])
    );

    const formadorByEdxUsername = new Map(
      formadores
        .map((user) => [String(user?.edx_username ?? "").trim().toLowerCase(), user])
        .filter(([username]) => username)
    );

    const allEvents = eventsRaw.map(cleanEvent);

    const eventCandidates = allEvents
      .filter((event) => event.category_key === "redfid")
      .filter((event) => discipline === "general" || event.disciplinas.includes(discipline));

    const encuentrosPicked = pickEncuentros(eventCandidates);

    const events = eventCandidates.filter((event) => {
      if (event.bucket !== "encuentro-nacional" && event.bucket !== "encuentro-virtual") return true;
      return encuentrosPicked.get(encuentroDedupKey(event))?.id === event.id;
    });

    const eventById = new Map(events.map((event) => [event.id, event]));

    const filteredAttendants = attendants.filter(
      (attendant) =>
        formadorById.has(String(attendant?.user_id ?? "").trim()) &&
        eventById.has(String(attendant?.event_id ?? "").trim())
    );

    const filteredAchievements = flattenAchievements(allAchievements).filter((achievement) =>
      formadorById.has(achievement.user_id)
    );

    const attByUser = groupBy(filteredAttendants, (attendant) => attendant?.user_id);
    const attByEvent = groupBy(filteredAttendants, (attendant) => attendant?.event_id);
    const achByUser = groupBy(filteredAchievements, (achievement) => achievement?.user_id);

    const coursesCatalog = flattenCourses(coursesRaw);
    const enrolledCoursesByUser = new Map();
    const approvedCoursesByUser = new Map();

    coursesCatalog
      .filter((course) => discipline === "general" || course.type === discipline || course.type === "general")
      .forEach((course) => {
        course.sections.forEach((section) => {
          const courseInfo = {
            id: section.course_id,
            course_id: section.course_id,
            course_key: course.course_key,
            section_key: section.section_key,
            nombre: course.name,
            disciplina: DISCIPLINE_LABEL[course.type] ?? formatTitle(course.type),
            version: section.title || "—",
            anio: section.year ? Number(section.year) : null,
            titulo: `${course.name || "Curso"}${section.title ? ` · ${section.title}` : ""}`,
            description: course.description,
          };

          section.enrolled_usernames.forEach((username) => {
            const user = formadorByEdxUsername.get(username);
            const id = String(user?.id ?? user?.user_id ?? "").trim();
            if (!id) return;
            if (!enrolledCoursesByUser.has(id)) enrolledCoursesByUser.set(id, []);
            enrolledCoursesByUser.get(id).push(courseInfo);
          });

          section.approved_usernames.forEach((username) => {
            const user = formadorByEdxUsername.get(username);
            const id = String(user?.id ?? user?.user_id ?? "").trim();
            if (!id) return;
            if (!approvedCoursesByUser.has(id)) approvedCoursesByUser.set(id, []);
            approvedCoursesByUser.get(id).push(courseInfo);
          });
        });
      });

    const talleresExpuestosByUser = new Map();

    events
      .filter((event) => event.bucket === "taller")
      .forEach((event) => {
        event.expositors.forEach((userId) => {
          if (!formadorById.has(userId)) return;

          if (!talleresExpuestosByUser.has(userId)) {
            talleresExpuestosByUser.set(userId, []);
          }

          talleresExpuestosByUser.get(userId).push(buildEventItem(event));
        });
      });


    const encuentrosByUser = new Map();
    const attendeesByEncuentro = new Map();
    const encuentroTitleById = new Map();

    events
      .filter((event) => event.bucket === "encuentro-nacional" || event.bucket === "encuentro-virtual")
      .forEach((event) => encuentroTitleById.set(event.id, event.titulo));

    filteredAttendants.forEach((attendant) => {
      const userKey = String(attendant?.user_id ?? "").trim();
      const event = eventById.get(String(attendant?.event_id ?? "").trim());

      if (!event || (event.bucket !== "encuentro-nacional" && event.bucket !== "encuentro-virtual")) return;

      if (!encuentrosByUser.has(userKey)) encuentrosByUser.set(userKey, new Set());
      if (!attendeesByEncuentro.has(event.id)) attendeesByEncuentro.set(event.id, new Set());

      encuentrosByUser.get(userKey).add(event.id);
      attendeesByEncuentro.get(event.id).add(userKey);
    });

    const presentersByEncuentro = new Map();

    filteredAchievements.forEach((achievement) => {
      const presentedTail = parsePresentedIn(achievement.name);
      if (!presentedTail) return;

      const encuentro = parseEncuentroName(presentedTail) ?? presentedTail;
      if (!presentersByEncuentro.has(encuentro)) presentersByEncuentro.set(encuentro, new Set());
      presentersByEncuentro.get(encuentro).add(achievement.user_id);
    });

    const nowYear = new Date().getFullYear();

    const participantes = formadores.map((user) => {
      const id = String(user?.id ?? user?.user_id ?? "").trim();
      const userAttendants = attByUser.get(id) ?? [];
      const userAchievements = achByUser.get(id) ?? [];
      const universities = userUniversities(user);
      const startComite = user?.comite_start_year ?? user?.["comité_start_year"] ?? null;
      const endComite = user?.comite_end_year ?? null;
      const regionRoman = cleanRegionKey(user?.region);
      const regionInfo = REGION_TO_CODE[regionRoman];
      const yearsExperience = user?.since ? nowYear - Number(user.since) : null;
      const standards = userStandards(user);
      const estandaresLabels = Object.values(standards).flatMap(({ labels }) => labels);

      const eventCounts = {
        talleres: 0,
        reuniones_comite: 0,
        reuniones_virtuales: 0,
        webinars: 0,
        encuentros: 0,
        otros: 0,
      };

      userAttendants.forEach((attendant) => {
        const event = eventById.get(String(attendant?.event_id ?? "").trim());
        if (!event) return;

        if (event.bucket === "taller") eventCounts.talleres += 1;
        else if (event.bucket === "actividad-comite") eventCounts.reuniones_comite += 1;
        else if (event.bucket === "reunion-virtual") eventCounts.reuniones_virtuales += 1;
        else if (event.bucket === "webinar") eventCounts.webinars += 1;
        else if (event.bucket === "encuentro-nacional" || event.bucket === "encuentro-virtual") eventCounts.encuentros += 1;
        else eventCounts.otros += 1;
      });

      const presentaciones = new Set(userAchievements.map((a) => parsePresentedIn(a.name)).filter(Boolean));
      const recursosCompartidos = Array.from(
        new Set(
          userAchievements
            .map((achievement) => String(achievement.name ?? "").trim())
            .filter((name) => {
              const text = name.toLowerCase();
              return (
                text.includes("ha compartido") &&
                (
                  text.includes("recursos") ||
                  text.includes("recurso")
                )
              );
            })
        )
      );
      const cursosInscritos = enrolledCoursesByUser.get(id) ?? [];
      const cursosAprobados = approvedCoursesByUser.get(id) ?? [];
      const talleresExpuestos = talleresExpuestosByUser.get(id) ?? [];
      const disciplinas = userDisciplines(user);

      return {
        id,
        nombre: String(user?.first_name ?? "").trim(),
        apellido: String(user?.last_name ?? "").trim(),
        nombre_completo: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),

        grado: degreeLabel(user?.degree),
        grado_key: degreeKey(user?.degree),
        titulo_profesional: formatTitle(user?.title ?? "—"),
        programa: prettyPrograma(user?.program_teaching_at ?? "—"),
        estandares: estandaresLabels.join(", ") || "—",
        estandares_keys: estandaresLabels.length
          ? Object.values(standards).flatMap(({ keys }) => keys)
          : [],

        estandar_basica: standards.estandar_basica.labels.join(", ") || "—",
        estandar_basica_keys: standards.estandar_basica.keys,

        estandar_media: standards.estandar_media.labels.join(", ") || "—",
        estandar_media_keys: standards.estandar_media.keys,

        estandar_lenguaje: standards.estandar_lenguaje.labels.join(", ") || "—",
        estandar_lenguaje_keys: standards.estandar_lenguaje.keys,
        disciplina: disciplineText(disciplinas),
        nivel: levelFromYears(yearsExperience),

        anio_union: safeDate(user?.created_at)?.getFullYear() ?? "—",
        anio_formador: user?.since ?? "—",

        comite: Boolean(user?.is_comite),
        start_comite: startComite,
        end_comite: endComite,
        comite_periodo: user?.is_comite
          ? startComite && endComite
            ? `${startComite}–${endComite}`
            : startComite
              ? `${startComite}–actualidad`
              : endComite
                ? `Hasta ${endComite}`
                : "Sí"
          : "No corresponde",

        pais: String(user?.pais ?? "").trim() || "—",
        region: String(user?.region ?? "").trim() || "—",
        comuna: String(user?.comuna ?? "").trim() || "—",
        region_roman: regionRoman,
        region_name: regionInfo?.name ?? "—",

        universidad: universities.length ? universities.map((uni) => uni.name).join(", ") : "—",
        universidad_keys: universities.map((uni) => uni.key),
        universidad_pais:
          Array.from(new Set(universities.map((uni) => uni.country).filter((x) => x !== "—"))).join(", ") || "—",
        universidad_cuech: universities.some((uni) => uni.is_cuech),
        universidad_cruch: universities.some((uni) => uni.is_cruch),

        cursos_inscritos: cursosInscritos.length,
        cursos_aprobados: cursosAprobados.length,
        cursos_realizados: cursosAprobados.length,
        finalizo_curso: cursosAprobados.length > 0,


        adjudico_fondo:
          Boolean(user?.adjudico_fondo) ||
          userAchievements.some((achievement) => {
            const text = String(achievement.name ?? "").trim().toLowerCase();
            return text.includes("adjudic") && (text.includes("fondo") || text.includes("fondos"));
          }),
        participa_sophia: userAchievements.some((achievement) => {
          const text = String(achievement.name ?? "").trim().toLowerCase();
          return text.includes("sophia") && text.includes("especial");
        }),

        encuentros_asistidos: eventCounts.encuentros,
        presentaciones_realizadas: presentaciones.size,
        talleres_asistidos: eventCounts.talleres,
        talleres_expuestos: talleresExpuestos.length,
        recursos_compartidos: recursosCompartidos.length,
        reuniones_asistidas_comite: eventCounts.reuniones_comite,
        reuniones_asistidas_virtuales: eventCounts.reuniones_virtuales,
        webinars_asistidos: eventCounts.webinars,
        otros_eventos_asistidos: eventCounts.otros,

        encuentros: eventCounts.encuentros,
        presentaciones: presentaciones.size,
        talleres: eventCounts.talleres,
        reuniones_comite: eventCounts.reuniones_comite,
        reuniones_virtuales: eventCounts.reuniones_virtuales,

        encuentros_lista: Array.from(encuentrosByUser.get(id) ?? []).map(
          (eventId) => encuentroTitleById.get(eventId) ?? eventId
        ),
        recursos_compartidos_lista: recursosCompartidos,
        talleres_expuestos_lista: talleresExpuestos,
      };
    });

    const participanteById = new Map(participantes.map((p) => [p.id, p]));

    const userDetailById = new Map(
      formadores.map((user) => {
        const id = String(user?.id ?? user?.user_id ?? "").trim();
        const participante = participanteById.get(id);
        const userAttendants = attByUser.get(id) ?? [];
        const userAchievements = achByUser.get(id) ?? [];
        const cursosInscritos = enrolledCoursesByUser.get(id) ?? [];
        const cursosAprobados = approvedCoursesByUser.get(id) ?? [];

        const approvedKeys = new Set(
          cursosAprobados.map((c) => `${c.course_key}__${c.section_key}__${c.course_id}`)
        );

        const cursos = cursosInscritos.map((c) => {
          const key = `${c.course_key}__${c.section_key}__${c.course_id}`;

          return {
            ...c,
            estado: approvedKeys.has(key) ? "aprobado" : "inscrito",
            aprobado: approvedKeys.has(key),
          };
        });

        cursosAprobados.forEach((c) => {
          const key = `${c.course_key}__${c.section_key}__${c.course_id}`;

          if (!cursos.some((x) => `${x.course_key}__${x.section_key}__${x.course_id}` === key)) {
            cursos.push({ ...c, estado: "aprobado", aprobado: true });
          }
        });

        cursos.sort((a, b) => {
          const yearDiff = Number(b?.anio ?? 0) - Number(a?.anio ?? 0);
          return yearDiff || String(a?.titulo ?? "").trim().localeCompare(String(b?.titulo ?? "").trim());
        });

        const detailEvents = {
          talleres: [],
          encuentros: [],
          reuniones_comite: [],
          reuniones_virtuales: [],
          webinars: [],
          otros_eventos: [],
        };

        userAttendants.forEach((attendant) => {
          const event = eventById.get(String(attendant?.event_id ?? "").trim());
          if (!event) return;

          const item = buildEventItem(event);

          if (event.bucket === "taller") detailEvents.talleres.push(item);
          else if (event.bucket === "encuentro-nacional" || event.bucket === "encuentro-virtual") detailEvents.encuentros.push(item);
          else if (event.bucket === "actividad-comite") detailEvents.reuniones_comite.push(item);
          else if (event.bucket === "reunion-virtual") detailEvents.reuniones_virtuales.push(item);
          else if (event.bucket === "webinar") detailEvents.webinars.push(item);
          else detailEvents.otros_eventos.push(item);
        });

        Object.values(detailEvents).forEach((list) => {
          list.sort((a, b) => {
            const ta = safeDate(a.inicio)?.getTime() ?? Number.POSITIVE_INFINITY;
            const tb = safeDate(b.inicio)?.getTime() ?? Number.POSITIVE_INFINITY;
            return ta !== tb ? ta - tb : a.titulo.localeCompare(b.titulo);
          });
        });

        const talleresExpuestos = (talleresExpuestosByUser.get(id) ?? []).map((taller) => ({
          id: taller.id,
          titulo: taller.titulo,
          year: taller.year,
          fecha: taller.inicio,
        }));

        const presentacionesList = Array.from(
          new Set(userAchievements.map((achievement) => parsePresentedIn(achievement.name)).filter(Boolean))
        );

        const recursosCompartidos = Array.from(
          new Set(
            userAchievements
              .map((achievement) => String(achievement.name ?? "").trim())
              .filter((name) => {
                const text = name.toLowerCase();
                return (
                  text.includes("ha compartido") &&
                  (
                    text.includes("recursos") ||
                    text.includes("recurso")
                  )
                );
              })
          )
        );

        return [
          id,
          {
            id,
            rawUser: user,

            nombre: participante?.nombre ?? String(user?.first_name ?? "").trim(),
            apellido: participante?.apellido ?? String(user?.last_name ?? "").trim(),
            nombre_completo: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),

            universidad: participante?.universidad ?? "—",
            universidad_keys: participante?.universidad_keys ?? [],
            universidad_pais: participante?.universidad_pais ?? "—",
            universidad_cuech: participante?.universidad_cuech ?? false,
            universidad_cruch: participante?.universidad_cruch ?? false,

            titulo_profesional: participante?.titulo_profesional ?? "—",
            grado: participante?.grado ?? "—",
            grado_key: participante?.grado_key ?? "—",
            programa: participante?.programa ?? "—",
            estandares: participante?.estandares ?? "—",
            estandares_keys: participante?.estandares_keys ?? [],

            estandar_basica: participante?.estandar_basica ?? "—",
            estandar_basica_keys: participante?.estandar_basica_keys ?? [],

            estandar_media: participante?.estandar_media ?? "—",
            estandar_media_keys: participante?.estandar_media_keys ?? [],

            estandar_lenguaje: participante?.estandar_lenguaje ?? "—",
            estandar_lenguaje_keys: participante?.estandar_lenguaje_keys ?? [],
            pais: participante?.pais ?? "—",
            ciudad: participante?.comuna ?? "—",
            region: participante?.region ?? "—",
            region_full: participante?.region_name ?? "—",
            region_roman: participante?.region_roman ?? cleanRegionKey(user?.region),
            nivel: formatTitle(participante?.nivel ?? "—"),

            anio_union: participante?.anio_union ?? "—",
            anio_formador: participante?.anio_formador ?? "—",
            comite: participante?.comite ?? false,
            start_comite: participante?.start_comite ?? null,
            end_comite: participante?.end_comite ?? null,
            comite_periodo: participante?.comite_periodo ?? "No corresponde",
            disciplina: participante?.disciplina ?? disciplineText(userDisciplines(user)),

            cursos,

            cursos_inscritos: cursosInscritos.length,
            cursos_aprobados: cursosAprobados.length,
            cursos_realizados: cursosAprobados.length,
            finalizo_curso: cursosAprobados.length > 0,

            adjudico_fondo: participante?.adjudico_fondo ?? false,
            participa_sophia: participante?.participa_sophia ?? false,

            resumen: {
              talleres_asistidos: detailEvents.talleres.length,
              talleres_expuestos: talleresExpuestos.length,
              encuentros_asistidos: detailEvents.encuentros.length,
              reuniones_asistidas_comite: detailEvents.reuniones_comite.length,
              reuniones_asistidas_virtuales: detailEvents.reuniones_virtuales.length,
              webinars_asistidos: detailEvents.webinars.length,
              otros_eventos_asistidos: detailEvents.otros_eventos.length,
              presentaciones_realizadas: presentacionesList.length,
              cursos_inscritos: cursosInscritos.length,
              cursos_realizados: cursosAprobados.length,
              cursos_aprobados: cursosAprobados.length,
              comite: participante?.comite ?? false,
              comite_periodo: participante?.comite_periodo ?? "No corresponde",
              recursos_compartidos: recursosCompartidos.length,
            },

            ...detailEvents,

            talleres_expuestos: talleresExpuestos,

            logros: userAchievements.map((achievement) => ({
              achievement_id: achievement.achievement_id,
              nombre: achievement.name,
            })),

            presentaciones: presentacionesList,
            recursos_compartidos: recursosCompartidos,
          },
        ];
      })
    );

    const joinedYears = participantes
      .map((p) => p.anio_union)
      .filter((year) => year && year !== "—")
      .sort((a, b) => Number(a) - Number(b));

    let totalAcumulado = 0;

    const growthByYear = countToArray(joinedYears, (year) => year)
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map(({ id, value }) => {
        totalAcumulado += value;
        return { year: id, new_formadores: value, total_acumulado: totalAcumulado };
      });

    const byRegionRoman = freqBy(formadores, (user) => cleanRegionKey(user?.region), "—");

    const dash = {
      total_formadores: participantes.length,
      by_level: ["novel", "intermedio", "experto", "—"].map((id) => ({
        id,
        value: participantes.filter((p) => p.nivel === id).length,
      })),
      by_degree: countToArray(participantes, (p) => p.grado_key),
      by_title: countToArray(participantes, (p) => p.titulo_profesional),
      by_program: countManyToArray(participantes, (p) => programLabels(p?.programa)),
      by_university: countManyToArray(participantes, (p) => String(p?.universidad ?? "").split(",").map((x) => x.trim())),
      by_region_roman: byRegionRoman,
      by_region_code: regionFreqToCode(byRegionRoman),
      growth_by_year: growthByYear,
    };

    const eventosBase = events.map((event) => {
      const formadoresNames = (attByEvent.get(event.id) ?? [])
        .map((attendant) => formadorById.get(String(attendant?.user_id ?? "").trim()))
        .filter(Boolean)
        .map((user) => `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      return {
        ...buildEventItem(event),
        tipo: event?.kind ?? event?.type ?? "—",
        formadores_qtty: formadoresNames.length,
        formadores: formadoresNames,
        source: "attendants",
        taller_year: null,
        taller_n: null,
        taller_card_title: null,
        taller_card_subtitle: null,
      };
    });

    const talleresByYear = groupBy(
      eventosBase.filter((event) => event.bucket === "taller"),
      (event) => event.year ?? "—"
    );

    const tallerDisciplineGroups = [
      {
        discipline: "matematica",
        discipline_label: "Matemática",
        match: (taller) =>
          (Array.isArray(taller?.disciplinas) ? taller.disciplinas : []).some((d) =>
            ["matematica", "parvularia"].includes(
              String(d ?? "").trim().toLowerCase()
            )
          ),
      },
      {
        discipline: "lenguaje",
        discipline_label: "Lenguaje",
        match: (taller) =>
          (Array.isArray(taller?.disciplinas) ? taller.disciplinas : []).some(
            (d) => String(d ?? "").trim().toLowerCase() === "lenguaje"
          ),
      },
    ];

    const eventos = [...eventosBase].sort((a, b) => b.formadores_qtty - a.formadores_qtty);

    const universidadesMap = new Map();

    participantes.forEach((participante) => {
      participante.universidad_keys.forEach((uniKey) => {
        const uni = getUniversityData(uniKey);
        if (!uni) return;

        if (!universidadesMap.has(uni.key)) {
          universidadesMap.set(uni.key, {
            id: uni.key,
            universidad: uni.name,
            universidad_full: uni.name,
            universidad_pais: uni.country,
            universidad_cuech: uni.is_cuech,
            universidad_cruch: uni.is_cruch,
            formadores_qtty: 0,
            formadores: [],
          });
        }

        const row = universidadesMap.get(uni.key);
        const nombre = `${participante?.nombre ?? ""} ${participante?.apellido ?? ""}`.trim();

        if (nombre && !row.formadores.includes(nombre)) row.formadores.push(nombre);
        row.formadores_qtty = row.formadores.length;
      });
    });

    const universidades = Array.from(universidadesMap.values())
      .map((uni) => ({ ...uni, formadores: uni.formadores.sort((a, b) => a.localeCompare(b)) }))
      .sort((a, b) => b.formadores_qtty - a.formadores_qtty);

    const regiones = Array.from(groupBy(participantes, (p) => p.region_roman).entries())
      .map(([roman, rows]) => {
        const regionInfo = REGION_TO_CODE[roman];

        return {
          id: regionInfo ? String(regionInfo.code) : "—",
          region_roman: roman,
          region: rows[0]?.region ?? roman ?? "—",
          region_name: regionInfo?.name ?? "—",
          formadores_qtty: rows.length,
          participantes: rows
            .map((p) => `${p?.nombre ?? ""} ${p?.apellido ?? ""}`.trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b)),
          universidades: countManyToArray(rows, (row) => String(row?.universidad ?? "").split(",").map((x) => x.trim())),
          titulos: countToArray(rows, (row) => row.titulo_profesional),
          programas: countManyToArray(rows, (row) => programLabels(row?.programa)),
        };
      })
      .sort((a, b) => b.formadores_qtty - a.formadores_qtty);

    const formacion = {
      niveles: groupWithNames(participantes, (p) => p.nivel),
      grados: groupWithNames(participantes, (p) => p.grado),
      titulos: groupWithNames(participantes, (p) => p.titulo_profesional),
      programas: groupProgramaCountList(participantes),
    };

    const eventosConAsistentes = eventos
      .map((event) => ({
        event_id: event.id,
        title: event.titulo,
        bucket: event.bucket,
        attendees: (attByEvent.get(event.id) ?? [])
          .map((attendant) => formadorById.get(String(attendant?.user_id ?? "").trim()))
          .filter(Boolean)
          .map((user) => ({
            id: String(user?.id ?? user?.user_id ?? "").trim(),
            nombre: String(user?.first_name ?? "").trim(),
            apellido: String(user?.last_name ?? "").trim(),
            nombre_completo: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          }))
          .sort((a, b) => a.nombre_completo.localeCompare(b.nombre_completo)),
        source: "attendants",
      }))
      .filter((event) => event.attendees.length > 0)
      .sort((a, b) => b.attendees.length - a.attendees.length);

    const presentaciones = Array.from(presentersByEncuentro.entries())
      .map(([encuentro, setUsers]) => ({
        encuentro,
        count: setUsers.size,
        list: Array.from(setUsers)
          .map((id) => formadorById.get(id))
          .filter(Boolean)
          .map((user) => `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => b.count - a.count);

    const cursosFinalizadosUsers = new Set(approvedCoursesByUser.keys());

    const cursosFinalizados = {
      id: "curso-finalizado",
      title: "Curso",
      count: cursosFinalizadosUsers.size,
      list: Array.from(cursosFinalizadosUsers)
        .map((id) => formadorById.get(id))
        .filter(Boolean)
        .map((user) => `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    };

    const cursosCatalogo = coursesCatalog
      .filter((course) => discipline === "general" || course.type === discipline || course.type === "general")
      .map((course) => ({
        ...course,
        sections: course.sections.map((section) => {
          const inscritos = section.enrolled_usernames
            .map((username) => formadorByEdxUsername.get(username))
            .filter(Boolean)
            .map((user) => `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));

          const aprobados = section.approved_usernames
            .map((username) => formadorByEdxUsername.get(username))
            .filter(Boolean)
            .map((user) => `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));

          return {
            ...section,
            inscritos_qtty: inscritos.length,
            aprobados_qtty: aprobados.length,
            inscritos,
            aprobados,
            inscritos_usernames: section.enrolled_usernames,
            aprobados_usernames: section.approved_usernames,
            inscritos_no_encontrados: section.enrolled_usernames.filter(
              (username) => !formadorByEdxUsername.has(username)
            ),
            aprobados_no_encontrados: section.approved_usernames.filter(
              (username) => !formadorByEdxUsername.has(username)
            ),
            count: aprobados.length,
            list: aprobados,
          };
        }),
      }))
      .filter((course) => course.sections.length > 0);

    const talleresPorAnio = Array.from(talleresByYear.entries())
      .filter(([year]) => year !== "—")
      .flatMap(([year, talleres]) =>
        tallerDisciplineGroups
          .map((group) => {
            const rows = talleres
              .filter(group.match)
              .sort((a, b) => {
                const ta = safeDate(a.inicio)?.getTime() ?? Number.POSITIVE_INFINITY;
                const tb = safeDate(b.inicio)?.getTime() ?? Number.POSITIVE_INFINITY;
                return ta !== tb ? ta - tb : a.titulo.localeCompare(b.titulo);
              })
              .map((taller, index) => ({
                ...taller,
                taller_year: Number(year),
                taller_discipline: group.discipline,
                taller_discipline_label: group.discipline_label,
                taller_n: index + 1,
                taller_card_title: `Taller ${index + 1}`,
                taller_card_subtitle: String(taller.titulo ?? "").trim() || "—",
              }));

            return {
              id: `${year}-${group.discipline}`,
              year: Number(year),
              discipline: group.discipline,
              discipline_label: group.discipline_label,
              title: `TALLERES DE APRENDIZAJE PROFESIONAL - RedFID ${group.discipline_label} ${year}`,
              talleres: rows,
            };
          })
          .filter((group) => group.talleres.length > 0)
      )
      .sort((a, b) => {
        const yearDiff = Number(b.year) - Number(a.year);
        if (yearDiff) return yearDiff;

        const order = {
          matematica: 0,
          lenguaje: 1,
        };

        return (order[a.discipline] ?? 9) - (order[b.discipline] ?? 9);
      });

    const encuentrosAsistencia = Array.from(attendeesByEncuentro.entries())
      .map(([encId, setUsers]) => ({
        encuentro: encuentroTitleById.get(encId) ?? encId,
        attendees: Array.from(setUsers)
          .map((id) => formadorById.get(id))
          .filter(Boolean)
          .map((user) => ({
            id: String(user?.id ?? user?.user_id ?? "").trim(),
            nombre: String(user?.first_name ?? "").trim(),
            apellido: String(user?.last_name ?? "").trim(),
            nombre_completo: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          }))
          .sort((a, b) => a.nombre_completo.localeCompare(b.nombre_completo)),
        attendees_qtty: setUsers.size,
        source: "attendants",
      }))
      .sort((a, b) => b.attendees_qtty - a.attendees_qtty);

    return {
      raw: {
        users,
        events: eventsRaw,
        attendants,
        all_achievements: allAchievements,
        courses: coursesRaw,
        coursesCatalog,
      },

      dash,

      tables: {
        participantes,
        universidades,
        eventos,
        talleres: eventos.filter((event) => event.bucket === "taller"),
      },

      discipline,
      country,

      views: {
        eventosConAsistentes,
        presentaciones,
        cursosFinalizados,
        cursosCatalogo,
        talleresPorAnio,
        encuentrosAsistencia,
      },

      formacion,
      regiones,

      index: {
        formadorByKey: formadorById,
        eventByKey: eventById,
        attByUser,
        attByEvent,
        achByUser,
        participanteById,
        userDetailById,
      },
    };
  }, [data, discipline, country]);

  return { loading, error, data: result, refreshData };
}